/**
 * `/auth/error` — the terminal sign-in failure page. Reached from `/auth/return` on an
 * `?error=oauth_failed&errorCode=<CODE>` arrival or a failed code exchange, and from a
 * kickoff that could not resolve a login URL. Never auto-retries: the user chooses.
 */
import { Box, Button, Container, Paper, Typography } from '@mui/material';
import { useLocation } from 'react-router-dom';
import { startCentralizedLogin } from '@/auth/loginKickoff';
import { useCopy } from '@/hooks/useCopy';

/**
 * `errorCode` → copy key + English fallback. The first four are saas-auth's OAuth deny
 * codes; the last two are the `brand.<domain>` return-origin prerequisite (genesis handoff
 * §2.1) surfacing as a rejected `login-url` request — an operator-actionable state, not a
 * user error, so it says so.
 */
const ERROR_BODY: Record<string, { key: string; fallback: string }> = {
  OAUTH_FAILED: {
    key: 'auth.error.oauthFailed',
    fallback: 'Sign-in could not be completed. Please try again.',
  },
  INVALID_OAUTH_STATE: {
    key: 'auth.error.invalidOauthState',
    fallback: 'The sign-in request expired or was invalid. Please start again.',
  },
  PROVIDER_NOT_ALLOWED: {
    key: 'auth.error.providerNotAllowed',
    fallback: "This sign-in provider isn't permitted for Brand Center.",
  },
  OAUTH_TENANT_MISMATCH: {
    key: 'auth.error.oauthTenantMismatch',
    fallback: 'This sign-in was issued for a different application.',
  },
  TENANT_CONTEXT_REQUIRED: {
    key: 'auth.error.tenantContextRequired',
    fallback:
      'Brand Center is not yet registered with the sign-in service. Please contact support.',
  },
  INVALID_RETURN_URL: {
    key: 'auth.error.invalidReturnUrl',
    fallback:
      'Brand Center is not yet registered with the sign-in service. Please contact support.',
  },
};

export function AuthError() {
  const { t } = useCopy();
  const errorCode = new URLSearchParams(useLocation().search).get('errorCode');
  const entry = errorCode ? ERROR_BODY[errorCode] : undefined;
  const body = entry
    ? t(entry.key, entry.fallback)
    : t('auth.errorBody', 'Your session could not be restored. Try signing in again.');

  return (
    <Container maxWidth="sm" sx={{ py: 8 }}>
      <Paper sx={{ p: 4 }} elevation={1}>
        <Typography variant="h1" component="h1" gutterBottom>
          {t('auth.errorTitle', 'Sign-in could not be completed')}
        </Typography>
        <Typography variant="body1" sx={{ mb: 3 }}>
          {body}
        </Typography>
        <Box>
          <Button variant="contained" onClick={() => void startCentralizedLogin('/')}>
            {t('auth.tryAgain', 'Try again')}
          </Button>
        </Box>
        {errorCode ? (
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 3 }}>
            {t('auth.errorReference', 'Reference: {{code}}', { code: errorCode })}
          </Typography>
        ) : null}
      </Paper>
    </Container>
  );
}
